import { useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Kill-switch for the order-confirmation Pay-by-Bank payment-switch feature.
// When off, the V2 confirmation modal, the CTA, the post-enrollment hand-off,
// the cart-approval write path, and the modal-reopen effect all become no-ops.
export const PBB_PAYMENT_SWITCH_FLAG = 'ecomm.order-confirmation.pbb-payment-switch.rollout';

// Kill-switch for the one-click (no-redirect) Pay-by-Bank approval flow inside the confirmation
// modal, independent of PBB_PAYMENT_SWITCH_FLAG. While off, the modal keeps redirecting to
// /pay/instore; once on-click one-click cart-token acquisition + approval are validated for a
// dispensary, this flag can be flipped without touching the redirect fallback.
export const PBB_ONE_CLICK_FLAG = 'ecomm.order-confirmation.pbb-one-click.rollout';

// Order-confirmation PBB CTA copy A/B/C test (control/speed/trust). See
// apps/marketplace/src/hooks/use-launch-darkly-experiment.ts.
export const PBB_CTA_EXPERIMENT_FLAG = 'ecomm.order-confirmation.pbb-cta.experiment';

export type PbbCtaExperimentVariant = 'control' | 'speed' | 'trust';

/**
 * Returns true once the flag has been observed `true` at any point during the component's
 * lifetime, even if a later render returns `false`/`undefined` (e.g. while LD is mid-identify
 * and the manifest is empty). Use this for any flag read that gates in-flight UX across an
 * async boundary; live `useFlags()` reads are still correct for purely render-time gates.
 *
 * Kill-switch semantics still hold: a session that never observes `true` keeps the latch
 * `false` and the consumer falls through to its legacy path.
 */
export function useLatchedFlag(flagKey: string): boolean {
  const flags = useFlags();
  const wasOnRef = useRef(false);
  if (flags[flagKey] === true) {
    wasOnRef.current = true;
  }
  return wasOnRef.current;
}
